import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { vipAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getProfilePicture } from '../utils/helpers';
import { FaCrown, FaEye, FaFire, FaGhost, FaBell, FaCog, FaUser, FaSignOutAlt, FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PaymentModal from '../components/PaymentModal';
import Header from '../components/Header';

const VIP = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();

  // Debug user state
  console.log('👤 VIP Page User State:', user, 'Auth Loading:', authLoading);

  // Debug user changes
  useEffect(() => {
    console.log('👤 VIP Header - User changed:', user?.id, user?.profile_picture);
  }, [user]);
  const [pricing, setPricing] = useState([]);
  const [vipStatus, setVipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Only call fetchData when user is available
  useEffect(() => {
    if (user && user.id) {
      console.log('✅ VIP Page: User authenticated, calling fetchData for user:', user.id);
      fetchData();
    } else {
      console.log('⏳ VIP Page: Waiting for user authentication...');
    }
  }, [user?.id]); // Only run when user ID changes

  // Force re-render when vipStatus changes
  useEffect(() => {
    console.log('🔄 VIP Status changed:', vipStatus);
  }, [vipStatus]);

  const fetchData = async () => {
    try {
      setError(null);
      setLoading(true);

      console.log('🔍 VIP Page: Starting API calls for user ID:', user.id);

      // Fetch both pricing and VIP status with user validation
      const [pricingRes, statusRes] = await Promise.all([
        vipAPI.getPricing(),
        vipAPI.getStatus()
      ]);

      // Validate responses
      if (!pricingRes || !statusRes) {
        throw new Error('Invalid response from server');
      }

      // Additional VIP status validation for this specific user
      if (statusRes.data) {
        const vipData = statusRes.data;

        // Verify that the response belongs to the current user
        if (vipData.userId && vipData.userId !== user.id) {
          console.error('🔍 VIP Page: VIP status mismatch! Expected user', user.id, 'but got', vipData.userId);
          throw new Error('VIP status validation failed - user mismatch');
        }

        // Check for expired VIP and auto-refresh if needed
        const currentTime = Date.now() / 1000;
        if (vipData.isVIP && (!vipData.expiration || vipData.expiration < currentTime)) {
          console.warn('🔍 VIP Page: Detected expired VIP status for user', user.id, ', refreshing...');
          // Force refresh VIP status if expired
          const refreshRes = await vipAPI.getStatus();
          if (refreshRes.data && refreshRes.data.userId === user.id) {
            statusRes.data = refreshRes.data;
          }
        }

        // Validate VIP data structure
        if (vipData.isVIP && (!vipData.daysRemaining || vipData.daysRemaining < 0)) {
          console.warn('🔍 VIP Page: Invalid VIP days remaining for user', user.id);
        }

        console.log(`🔍 VIP Page: VIP status validated for user ${user.id}: ${vipData.isVIP ? 'ACTIVE' : 'INACTIVE'}${vipData.isVIP ? ` (${vipData.daysRemaining || 0} days remaining)` : ''}`);
      }

      console.log('📦 VIP Pricing Response:', pricingRes.data);
      console.log('👑 VIP Status Response:', statusRes.data);
      console.log('✅ VIP Status isVIP:', statusRes.data?.isVIP, 'Type:', typeof statusRes.data?.isVIP);

      setPricing(pricingRes.pricing || []);
      setVipStatus(statusRes.data);

      // Force re-render by creating a new object reference
      setVipStatus(prev => ({ ...prev, ...statusRes.data }));

    } catch (error) {
      console.error('🔍 VIP Page: Failed to fetch VIP data for user', user?.id, ':', error);
      setError(error.message || 'Failed to load VIP information');

      // Auto retry logic (max 2 retries)
      if (retryCount < 2) {
        console.log(`🔍 VIP Page: Retrying VIP data fetch (${retryCount + 1}/2) for user`, user?.id);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchData();
        }, 1000 * (retryCount + 1)); // Progressive delay
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    fetchData();
  };

  const handleSubscribe = (pkg) => {
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    toast.success('VIP subscription activated!');
    setShowPaymentModal(false);

    // Refresh VIP status to show updated information
    try {
      const statusRes = await vipAPI.getStatus();
      if (statusRes.data && isValidVIPStatus(statusRes.data)) {
        setVipStatus(statusRes.data);
      }
    } catch (error) {
      console.error('Failed to refresh VIP status:', error);
    }
  };

  const handleCancelVIP = async () => {
    if (!window.confirm('Are you sure you want to cancel your VIP subscription? You will lose access to VIP features immediately.')) {
      return;
    }

    try {
      setLoading(true);
      await vipAPI.cancel();
      toast.success('VIP subscription cancelled successfully');

      // Refresh VIP status
      const statusRes = await vipAPI.getStatus();
      setVipStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to cancel VIP:', error);
      toast.error('Failed to cancel VIP subscription');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while fetching VIP data or if no user yet
  if (loading || !user) {
    return (
      <div className="matchus-vip min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="vip-loading-container">
            <div className="vip-loading-spinner"></div>
            <div className="vip-loading-content">
              <h3 className="vip-loading-title">Checking Your VIP Status</h3>
              <p className="vip-loading-subtitle">
                {retryCount > 0 ? `Retrying... (${retryCount}/2)` : 'Please wait while we verify your account'}
              </p>
              {retryCount > 0 && (
                <p className="vip-loading-retry">Connection issue detected. Retrying automatically...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && retryCount >= 2) {
    return (
      <div className="matchus-vip min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="vip-error-container">
            <div className="vip-error-icon">
              <FaExclamationTriangle className="text-red-500 text-4xl mx-auto" />
            </div>
            <h3 className="vip-error-title">Unable to Load VIP Information</h3>
            <p className="vip-error-message">
              We're having trouble checking your VIP status. This might be due to a network issue or server maintenance.
            </p>
            <div className="vip-error-actions">
              <button onClick={handleRetry} className="vip-retry-btn">
                <FaRedo className="mr-2" />
                Try Again
              </button>
            </div>
            <p className="vip-error-help">
              If the problem persists, please contact support or try refreshing the page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced VIP status validation with user verification
  const isValidVIPStatus = (status) => {
    return status &&
           typeof status.isVIP === 'boolean' &&
           status.isVIP === true &&
           status.expiration &&
           status.daysRemaining >= 0 &&
           status.userId === user?.id; // Verify status belongs to current user
  };

  // Debug: Log VIP status for troubleshooting
  console.log('🔍 VIP Page Debug:', {
    vipStatus,
    userId: user?.id,
    isVIP: vipStatus?.isVIP,
    expiration: vipStatus?.expiration,
    daysRemaining: vipStatus?.daysRemaining,
    userIdMatch: vipStatus?.userId === user?.id,
    isValidVIPStatus: vipStatus ? isValidVIPStatus(vipStatus) : false
  });

  // Show VIP status if user is already VIP
  // Enhanced check with debug logging
  console.log('🎯 VIP Check Debug:', {
    vipStatus,
    isVIP: vipStatus?.isVIP,
    isVIPType: typeof vipStatus?.isVIP,
    userId: user?.id,
    vipStatusUserId: vipStatus?.userId,
    userIdMatch: vipStatus?.userId === user?.id,
    conditionResult: vipStatus?.isVIP === true && vipStatus?.userId === user?.id,
    finalCondition: Boolean(vipStatus && vipStatus.isVIP === true && vipStatus.userId === user?.id)
  });

  // Show VIP status if user is already VIP
  // Enhanced check with debug logging
  console.log('🎯 VIP Check Debug:', {
    vipStatus,
    isVIP: vipStatus?.isVIP,
    isVIPType: typeof vipStatus?.isVIP,
    userId: user?.id,
    vipStatusUserId: vipStatus?.userId,
    userIdMatch: vipStatus?.userId === user?.id,
    conditionResult: vipStatus?.isVIP === true && vipStatus?.userId === user?.id
  });

  // VIP check with fallback to AuthContext user data
  console.log('🎯 VIP Check: user.is_vip =', user?.is_vip, 'vipStatus.isVIP =', vipStatus?.isVIP);

  // Primary: API response, Fallback: AuthContext user data
  const isVIPFromAPI = vipStatus && vipStatus.isVIP === true && vipStatus.userId === user?.id;
  const isVIPFromUser = user && user.is_vip === 1;

  if (isVIPFromAPI || isVIPFromUser) {
    return (
      <div className="matchus-vip min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <Header title="VIP Membership" />

        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="relative inline-block mb-6">
            <img
              key={`vip-${user?.id || 'guest'}`}
              src={getProfilePicture(user)}
              className="w-24 h-24 rounded-full border-4 border-white shadow-lg mx-auto"
              alt="Profile"
            />
              <div className="absolute -top-3 -right-3 bg-yellow-500 rounded-full p-3">
                <FaCrown className="text-white text-xl" />
              </div>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-2">You're Already a VIP Member! 🎉</h1>
            <p className="text-gray-600 text-lg mb-4">
              Enjoy all the premium features and benefits of your VIP membership
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 font-medium">
                Your VIP privileges will expire on <span className="font-bold">
                  {(() => {
                    const expirationDate = vipStatus?.expiration || user?.vip_expiration;
                    if (expirationDate) {
                      return new Date(expirationDate + (expirationDate.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    }
                    return 'Unknown Date';
                  })()}
                </span>
              </p>
            </div>

            <div className="vip-status-card bg-white rounded-lg shadow-md border border-yellow-200 p-6 mt-8 max-w-md mx-auto">
              <div className="flex items-center justify-center mb-4">
                <FaCrown className="text-3xl text-yellow-500 mr-3" />
                <div className="text-left">
                  <div className="text-lg font-semibold text-gray-900 vip-status-valid">VIP Status</div>
                  <div className="text-sm text-gray-600">Active Membership</div>
                </div>
              </div>

              {/* Expiry Warning */}
              {vipStatus.daysRemaining <= 7 && (
                <div className="vip-expiry-warning">
                  <FaExclamationTriangle className="vip-expiry-warning-icon" />
                  <span className="vip-expiry-warning-text">
                    {vipStatus.daysRemaining <= 3
                      ? `Expires in ${vipStatus.daysRemaining} days - Renew now!`
                      : `Expires in ${vipStatus.daysRemaining} days`
                    }
                  </span>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                {(() => {
                  // Use API response if available, otherwise fallback to user data
                  const expirationDate = vipStatus?.expiration || user?.vip_expiration;
                  const daysRemaining = vipStatus?.daysRemaining;

                  // Calculate days remaining if not provided by API
                  let calculatedDays = daysRemaining;
                  if (!calculatedDays && expirationDate) {
                    const expDate = new Date(expirationDate + (expirationDate.includes('T') ? '' : 'T23:59:59'));
                    const now = new Date();
                    calculatedDays = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
                  }

                  return (
                    <>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Days Remaining</span>
                        <span className={`font-semibold ${calculatedDays <= 7 ? 'vip-status-expiring' : 'text-yellow-600'}`}>
                          {calculatedDays || 0} days
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Expires</span>
                        <span className="font-semibold text-gray-900">
                          {expirationDate ? new Date(expirationDate + (expirationDate.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Cancel VIP Button */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <button
                  onClick={handleCancelVIP}
                  disabled={loading}
                  className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  {loading ? 'Cancelling...' : 'Cancel VIP Subscription'}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  This will immediately revoke your VIP privileges
                </p>
              </div>
            </div>
          </div>

          {/* VIP Features Reminder */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">
              Your VIP Benefits
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="bg-yellow-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <FaCrown className="text-2xl text-yellow-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">VIP Badge</h3>
                <p className="text-gray-600">Exclusive crown badge on your profile</p>
              </div>

              <div className="text-center">
                <div className="bg-blue-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <FaEye className="text-2xl text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">See Who Viewed You</h3>
                <p className="text-gray-600">Discover who has been checking your profile</p>
              </div>

              <div className="text-center">
                <div className="bg-red-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <FaFire className="text-2xl text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Spotlight Feature</h3>
                <p className="text-gray-600">Get featured prominently in search results</p>
              </div>

              <div className="text-center">
                <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <FaGhost className="text-2xl text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ghost Mode</h3>
                <p className="text-gray-600">Browse anonymously without being seen</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="matchus-vip min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      {/* Professional Header */}
      <Header title="VIP Membership" />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
                  <img
                    src={getProfilePicture(user)}
              className="w-20 h-20 rounded-full border-4 border-white shadow-lg mx-auto"
                    alt="Profile"
                  />
            <div className="absolute -top-2 -right-2 bg-yellow-500 rounded-full p-2">
              <FaCrown className="text-white text-sm" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Become a VIP Member</h1>
          <p className="text-gray-600 max-w-md mx-auto">
            Unlock exclusive features and stand out from the crowd with premium benefits
          </p>
        </div>


        {/* Pricing Cards */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">Choose Your Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pricing.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white rounded-lg shadow-md border-2 border-yellow-200 hover:border-yellow-400 hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-yellow-400 to-orange-400 p-4 text-center">
                  <div className="text-white">
                    <div className="text-2xl font-bold mb-1">{pkg.duration}</div>
                    <div className="text-sm opacity-90">Days</div>
                  </div>
                </div>
                <div className="p-6 text-center">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-1">
                      ${pkg.price}
                    </div>
                    <div className="text-gray-600">one-time payment</div>
                  </div>

                        <button
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-medium py-3 px-6 rounded-md transition-all duration-200 transform hover:scale-105"
                          onClick={() => handleSubscribe(pkg)}
                        >
                    Subscribe Now
                        </button>
                </div>
                      </div>
                    ))}
                  </div>
        </div>

        {/* Main Features */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">
            VIP Exclusive Features
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* VIP Badge */}
            <div className="text-center">
              <div className="bg-yellow-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaCrown className="text-3xl text-yellow-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">VIP Badge</h3>
              <p className="text-gray-600">
                Get a special crown badge on your profile to show your premium status
              </p>
            </div>

            {/* See Visitors */}
            <div className="text-center">
              <div className="bg-blue-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaEye className="text-3xl text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">See Who Viewed You</h3>
              <p className="text-gray-600">
                Discover who has been checking out your profile and get insights
              </p>
                    </div>

            {/* Spotlight */}
            <div className="text-center">
              <div className="bg-red-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FaFire className="text-3xl text-red-500" />
                    </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Spotlight Feature</h3>
              <p className="text-gray-600">
                Get featured prominently in search results and increase visibility
              </p>
                    </div>
                  </div>

          {/* Secondary Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Popular User */}
            <div className="text-center">
              <div className="bg-purple-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">⭐</span>
                    </div>
              <h4 className="font-semibold text-gray-900 mb-1">Popular User</h4>
              <p className="text-sm text-gray-600">Appear as a popular user in searches</p>
                    </div>

            {/* Featured Placement */}
            <div className="text-center">
              <div className="bg-green-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">📍</span>
                    </div>
              <h4 className="font-semibold text-gray-900 mb-1">Featured Placement</h4>
              <p className="text-sm text-gray-600">Get priority placement in matches</p>
                  </div>

            {/* Ghost Mode */}
            <div className="text-center">
              <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <FaGhost className="text-2xl text-gray-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Ghost Mode</h4>
              <p className="text-sm text-gray-600">Browse anonymously without being seen</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready to Stand Out?</h3>
          <p className="mb-6 opacity-90">
            Join thousands of users who have upgraded to VIP and found better matches faster.
          </p>
          <div className="text-sm opacity-75">
            🔒 Secure payments • ⚡ Instant activation • 💎 Premium benefits
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          package={selectedPackage}
          type="vip"
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default VIP;

